import { type JSONContent } from '@tiptap/core';

export const WORKSPACE_AGENT_GUIDED_VARIANT_ORIGINAL_NAME_PLACEHOLDER = '{original name}';

/** When an edit has no descriptive suffix, titles still need a change marker. */
export const WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_RENAME_SUFFIX = 'edited';

export const WORKSPACE_AGENT_GUIDED_VARIANT_TITLE_SUFFIX_SOURCE = {
  DEFAULT: 'default',
  USER: 'user',
} as const;

export type WorkspaceAgentGuidedVariantTitleSuffixSource =
  (typeof WORKSPACE_AGENT_GUIDED_VARIANT_TITLE_SUFFIX_SOURCE)[keyof typeof WORKSPACE_AGENT_GUIDED_VARIANT_TITLE_SUFFIX_SOURCE];

export type WorkspaceAgentGuidedVariantTitleSuffix = {
  source: WorkspaceAgentGuidedVariantTitleSuffixSource;
  value: string;
};

const resolveWorkspaceAgentGuidedVariantTitleDescriptor = (suffix: WorkspaceAgentGuidedVariantTitleSuffix): string =>
  suffix.value.length > 0 ? suffix.value : WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_RENAME_SUFFIX;

export const formatWorkspaceAgentGuidedVariantRenameTargetForPrompt = (
  suffix: WorkspaceAgentGuidedVariantTitleSuffix,
): string => {
  const descriptor = resolveWorkspaceAgentGuidedVariantTitleDescriptor(suffix);

  if (suffix.source === WORKSPACE_AGENT_GUIDED_VARIANT_TITLE_SUFFIX_SOURCE.USER) {
    return `"${WORKSPACE_AGENT_GUIDED_VARIANT_ORIGINAL_NAME_PLACEHOLDER}${descriptor}"`;
  }

  return `"${WORKSPACE_AGENT_GUIDED_VARIANT_ORIGINAL_NAME_PLACEHOLDER} - ${descriptor}"`;
};

export const WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT = {
  JPG: 'jpg',
  ORIGINAL: 'original',
  PNG: 'png',
  WEBP: 'webp',
} as const;

export type WorkspaceAgentGuidedVariantOutputFormat =
  (typeof WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT)[keyof typeof WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT];

type WorkspaceAgentGuidedVariantImageOutputFormat = Exclude<
  WorkspaceAgentGuidedVariantOutputFormat,
  typeof WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL
>;

const IMAGE_OUTPUT_FORMATS = [
  WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.JPG,
  WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.PNG,
  WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.WEBP,
] as const satisfies readonly WorkspaceAgentGuidedVariantImageOutputFormat[];

export const OUTPUT_FORMAT_LABEL: Record<WorkspaceAgentGuidedVariantOutputFormat, string> = {
  [WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.JPG]: 'JPG',
  [WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL]: 'Original',
  [WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.PNG]: 'PNG',
  [WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.WEBP]: 'WEBP',
};

/** Stable start of the sentence builders write. Format is inserted in front of it; the suffix is not parsed back out. */
const TITLE_SENTENCE_START = 'Set the title of each new asset to ';

export type WorkspaceAgentGuidedVariantOutput = {
  format: WorkspaceAgentGuidedVariantOutputFormat;
  suffix: string;
};

/**
 * A suffix the user types replaces the edit's default (`no-bg`, `{size}`, the filter name)
 * and is concatenated onto `{original name}` with no extra characters.
 * An empty suffix leaves the default, joined as `{original name} - {descriptor}`.
 * `{size}` is the only token, and only for multi-size edits.
 */
export const resolveWorkspaceAgentGuidedVariantPromptRenameSuffix = (
  defaultSuffix: string,
  output: WorkspaceAgentGuidedVariantOutput,
): WorkspaceAgentGuidedVariantTitleSuffix => {
  const suffix = output.suffix.trim();

  if (suffix.length > 0) {
    return { source: WORKSPACE_AGENT_GUIDED_VARIANT_TITLE_SUFFIX_SOURCE.USER, value: suffix };
  }

  return { source: WORKSPACE_AGENT_GUIDED_VARIANT_TITLE_SUFFIX_SOURCE.DEFAULT, value: defaultSuffix };
};

export const WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT: WorkspaceAgentGuidedVariantOutput = {
  format: WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL,
  suffix: '',
};

export const isWorkspaceAgentGuidedVariantOutputFormat = (
  value: string,
): value is WorkspaceAgentGuidedVariantOutputFormat =>
  IMAGE_OUTPUT_FORMATS.some((format) => format === value) ||
  value === WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL;

export const getWorkspaceAgentGuidedVariantOutputFormats = ({
  allowsJpeg,
}: {
  allowsJpeg: boolean;
}): readonly WorkspaceAgentGuidedVariantOutputFormat[] => [
  WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL,
  ...IMAGE_OUTPUT_FORMATS.filter((format) => allowsJpeg || format !== WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.JPG),
];

export const getWorkspaceAgentGuidedVariantOutputOptionsLabel = ({
  allowsFormat,
}: {
  allowsFormat: boolean;
}): string => (allowsFormat ? 'Add suffix and format' : 'Add suffix');

type WorkspaceAgentGuidedVariantOutputConstraints = {
  allowsFormat: boolean;
  allowsJpeg: boolean;
};

export const hasWorkspaceAgentGuidedVariantChosenImageFormat = (output: WorkspaceAgentGuidedVariantOutput): boolean =>
  output.format !== WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL;

/**
 * JPG cannot hold an alpha channel, so a transparent cutout drops it from the list and from a saved choice.
 * Image formats cannot wrap a video the edit will still emit, so those edits drop format entirely.
 */
export const resolveWorkspaceAgentGuidedVariantOutput = (
  output: WorkspaceAgentGuidedVariantOutput,
  { allowsFormat, allowsJpeg }: WorkspaceAgentGuidedVariantOutputConstraints,
): WorkspaceAgentGuidedVariantOutput => {
  if (!allowsFormat) {
    if (output.format === WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL) {
      return output;
    }

    return { ...output, format: WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL };
  }

  const formats = getWorkspaceAgentGuidedVariantOutputFormats({ allowsJpeg });

  if (formats.includes(output.format)) {
    return output;
  }

  return { ...output, format: WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL };
};

export const formatWorkspaceAgentGuidedVariantOutputSummary = (output: WorkspaceAgentGuidedVariantOutput): string => {
  const suffix = output.suffix.trim();
  const formatLabel =
    output.format === WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL ? '' : OUTPUT_FORMAT_LABEL[output.format];

  return [suffix, formatLabel].filter((part) => part.length > 0).join(' · ');
};

export const buildWorkspaceAgentGuidedVariantRenameInstruction = (
  suffix: WorkspaceAgentGuidedVariantTitleSuffix,
): string =>
  `${TITLE_SENTENCE_START}${formatWorkspaceAgentGuidedVariantRenameTargetForPrompt(suffix)} when you save it.`;

const buildFormatInstruction = (format: WorkspaceAgentGuidedVariantImageOutputFormat): string =>
  `Create the file as ${OUTPUT_FORMAT_LABEL[format]}.`;

/** Place the format sentence in front of the title sentence the builder already wrote. Do not rewrite that title sentence. */
const insertFormatInstruction = (content: string, formatInstruction: string): string => {
  const renameIndex = content.lastIndexOf(TITLE_SENTENCE_START);

  if (renameIndex === -1) {
    const trimmed = content.trimEnd();

    return trimmed.length > 0 ? `${trimmed} ${formatInstruction}` : formatInstruction;
  }

  const before = content.slice(0, renameIndex);
  const trimmedBefore = before.trimEnd();
  const rename = content.slice(renameIndex);

  if (trimmedBefore.length === 0) {
    const leadingSpace = before.length > 0 ? ' ' : '';

    return `${leadingSpace}${formatInstruction} ${rename}`;
  }

  return `${trimmedBefore} ${formatInstruction} ${rename}`;
};

type WorkspaceAgentGuidedVariantJsonApplyResult = {
  applied: boolean;
  nodes: JSONContent[];
};

const applyFormatInstructionInJsonNodes = (
  nodes: readonly JSONContent[],
  formatInstruction: string,
): WorkspaceAgentGuidedVariantJsonApplyResult => {
  let applied = false;
  const nextNodes = nodes.map((node) => {
    if (applied) {
      return node;
    }

    if (node.type === 'text' && typeof node.text === 'string' && node.text.includes(TITLE_SENTENCE_START)) {
      applied = true;

      return { ...node, text: insertFormatInstruction(node.text, formatInstruction) };
    }

    if (!node.content) {
      return node;
    }

    const nested = applyFormatInstructionInJsonNodes(node.content, formatInstruction);

    if (!nested.applied) {
      return node;
    }

    applied = true;

    return { ...node, content: nested.nodes };
  });

  return { applied, nodes: nextNodes };
};

export const applyWorkspaceAgentGuidedVariantOutputInstruction = ({
  content,
  output,
}: {
  content: string;
  output: WorkspaceAgentGuidedVariantOutput;
}): string => {
  if (output.format === WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL) {
    return content;
  }

  return insertFormatInstruction(content, buildFormatInstruction(output.format));
};

const appendFormatInstructionToJsonContent = (
  jsonContent: readonly JSONContent[],
  formatInstruction: string,
): JSONContent[] => {
  if (jsonContent.length === 0) {
    return [{ content: [{ text: formatInstruction, type: 'text' }], type: 'paragraph' }];
  }

  const lastIndex = jsonContent.length - 1;
  const last = jsonContent[lastIndex];
  const nodes = last.content ?? [];
  const lastNode = nodes[nodes.length - 1];
  const nextNodes =
    lastNode?.type === 'text' && typeof lastNode.text === 'string'
      ? [...nodes.slice(0, -1), { ...lastNode, text: insertFormatInstruction(lastNode.text, formatInstruction) }]
      : [...nodes, { text: ` ${formatInstruction}`, type: 'text' }];

  return [...jsonContent.slice(0, lastIndex), { ...last, content: nextNodes }];
};

export const applyWorkspaceAgentGuidedVariantOutputInstructionToJsonContent = ({
  jsonContent,
  output,
}: {
  jsonContent: readonly JSONContent[];
  output: WorkspaceAgentGuidedVariantOutput;
}): JSONContent[] => {
  if (output.format === WORKSPACE_AGENT_GUIDED_VARIANT_OUTPUT_FORMAT.ORIGINAL) {
    return [...jsonContent];
  }

  const formatInstruction = buildFormatInstruction(output.format);
  const withFormat = applyFormatInstructionInJsonNodes(jsonContent, formatInstruction);

  if (withFormat.applied) {
    return withFormat.nodes;
  }

  return appendFormatInstructionToJsonContent(jsonContent, formatInstruction);
};
